import React, { useState, useEffect } from 'react';
import { 
  Users, Search, RefreshCw, Clock, 
  CheckCircle, XCircle, Mail, Phone, Building 
} from 'lucide-react';

// --- Types ---
interface Seller {
  id: number;
  companyName: string;
  username: string;
  email: string;
  mobile: string;
  isVerified: boolean;
  
  // 🔥 Ye fields backend se aani chahiye (DTO ke through)
  lastActiveTime?: string; // ISO String (e.g. 2023-10-25T10:00:00)
  totalDurationMinutes?: number; // Total time spent on portal
}

const SellerListPage: React.FC = () => {
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // --- 1. Fetch Sellers from Spring Boot (2027) ---
  const fetchSellers = async () => {
    setLoading(true);
    try {
      // Backend par ek endpoint bana hona chahiye jo sellers + unka session data bheje
      const response = await fetch('http://localhost:2027/api/admin/sellers-activity');
      if (response.ok) {
        const data = await response.json();
        setSellers(data);
      } else {
        console.error("Failed to fetch sellers");
      }
    } catch (error) {
      console.error("Network Error", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSellers();
    
    // Optional: Auto-refresh list every 1 minute to update status
    const interval = setInterval(fetchSellers, 60000); 
    return () => clearInterval(interval);
  }, []);

  // --- 2. Helper: Check if Online (Last 5 mins) ---
  const isUserOnline = (lastActive?: string) => {
    if (!lastActive) return false;
    const lastSeenDate = new Date(lastActive);
    const now = new Date();
    const diffMinutes = (now.getTime() - lastSeenDate.getTime()) / (1000 * 60);
    
    // Agar 5 minute ke andar active tha, to Online hai
    return diffMinutes <= 5; 
  };

  // --- 3. Helper: Format "Last Seen" ---
  const formatLastSeen = (dateString?: string) => {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    const now = new Date();
    const diffSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffSeconds < 60) return "Just now";
    if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)} mins ago`;
    if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)} hrs ago`;
    return date.toLocaleDateString(); 
  };

  // --- 4. Helper: Format Duration (Minutes -> Hrs:Mins) ---
  const formatDuration = (minutes?: number) => {
    if (!minutes) return "0m";
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
  };

  // --- Filter Logic ---
  const filteredSellers = sellers.filter(s => 
    s.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      
      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6">
        <div>
           <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
             <Users className="text-blue-600" /> Seller Monitoring
           </h1>
           <p className="text-sm text-gray-500">Track seller activity and online status.</p>
        </div>
        
        <div className="flex gap-3 mt-4 md:mt-0 w-full md:w-auto">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
            <input 
              type="text" 
              placeholder="Search Company or Email..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
            />
          </div>
          <button 
            onClick={fetchSellers} 
            className="p-2 bg-white border rounded-lg hover:bg-gray-100 text-gray-600 transition"
            title="Refresh List"
          >
            <RefreshCw size={20} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-gray-100 text-gray-600 text-xs uppercase tracking-wider">
              <th className="p-4 font-semibold">Status</th>
              <th className="p-4 font-semibold">Company / Seller</th>
              <th className="p-4 font-semibold">Contact Info</th>
              <th className="p-4 font-semibold">Last Seen</th>
              <th className="p-4 font-semibold">Total Active Time</th>
              <th className="p-4 font-semibold text-center">Verification</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
               <tr><td colSpan={6} className="p-8 text-center text-gray-500">Loading sellers...</td></tr>
            ) : filteredSellers.length === 0 ? (
               <tr><td colSpan={6} className="p-8 text-center text-gray-500">No sellers found.</td></tr>
            ) : (
              filteredSellers.map((seller) => {
                const online = isUserOnline(seller.lastActiveTime);
                
                return (
                  <tr key={seller.id} className="hover:bg-blue-50/50 transition duration-150">
                    
                    {/* 1. Status Column */}
                    <td className="p-4">
                      <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold w-fit ${
                        online ? 'bg-green-100 text-red-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        <div className={`w-2.5 h-2.5 rounded-full ${
        online ? 'bg-red-600 animate-pulse shadow-[0_0_8px_rgba(220,38,38,0.8)]' : 'bg-gray-400'
    }`}></div>
                        {online ? 'ONLINE' : 'OFFLINE'}
                      </div>
                    </td>

                    {/* 2. Seller Info */}
                    <td className="p-4">
                      <div className="font-bold text-gray-800">{seller.companyName || "No Company Name"}</div>
                      <div className="text-xs text-gray-500">{seller.username}</div>
                    </td>

                    {/* 3. Contact */}
                    <td className="p-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                        <Mail size={14} className="text-blue-400"/> {seller.email}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Phone size={14} className="text-green-400"/> {seller.mobile}
                      </div>
                    </td>

                    {/* 4. Last Seen */}
                    <td className="p-4 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Clock size={14} className="text-gray-400"/>
                        {formatLastSeen(seller.lastActiveTime)}
                      </div>
                    </td>

                    {/* 5. Total Active Time (Ranking Metric) */}
                    <td className="p-4">
                      <span className="bg-indigo-50 text-indigo-700 px-3 py-1 rounded-md text-sm font-semibold border border-indigo-100">
                        {formatDuration(seller.totalDurationMinutes)}
                      </span>
                    </td>

                    {/* 6. Verification */}
                    <td className="p-4 text-center">
                      {seller.isVerified ? (
                        <span className="text-green-500 flex justify-center" title="Verified Seller">
                          <CheckCircle size={20} />
                        </span>
                      ) : (
                        <span className="text-red-400 flex justify-center" title="Not Verified">
                          <XCircle size={20} />
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SellerListPage;